import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useServerData — Level 2. The server-state layer this app was missing.
 *
 * THE BUG THIS EXISTS TO KILL
 * Every page here fetches in a `useEffect(..., [])` and never fetches again.
 * That treats a server response as MEMORY (fetched once, held forever) when it
 * is really a CACHE (cheap to re-read, expected to change underneath you). So a
 * tab left open renders a snapshot of whenever it mounted — indefinitely, with
 * no signal that it has drifted.
 *
 * Live instance: the whereabouts board refreshes nightly. A teammate set his
 * location, the overnight job picked it up, and the tab still showed the old
 * city — because the page had been open since before the refresh. Four people
 * hit this before we understood it, and the page's own subtitle said "Updated
 * overnight", which is true of the DATA and false of the VIEW.
 *
 * WHY A HOOK AND NOT A FIX IN THE PAGE
 * Eight+ pages have this same shape. Patching the one page where it was noticed
 * fixes one symptom and leaves the class alive; the next page re-invents it
 * (probably differently). Revalidation is a cross-cutting server-state concern,
 * so it belongs at L2, where any page can inherit it. Pages stay thin.
 *
 * WHAT IT DOES
 *   - fetches on mount
 *   - REVALIDATES when the tab becomes visible / regains focus (the reconciler:
 *     you looked at it again, so re-read the surface)
 *   - exposes refresh() for an explicit user action
 *   - optional polling for genuinely live data (off by default)
 *
 * Cost note: revalidation is only as expensive as `fetcher`. For whereabouts
 * that is a materialized-snapshot read (one SQL row), NOT a live calendar
 * fan-out — so focus-revalidation is quota-safe. A caller whose fetcher is
 * expensive should raise `minIntervalMs` or leave polling off.
 *
 * @param {Function} fetcher            async () => data. MUST be stable
 *                                      (useCallback) or pass `deps`.
 * @param {object}   [options]
 * @param {any[]}    [options.deps=[]]           refetch when these change
 * @param {boolean}  [options.revalidateOnFocus=true]
 * @param {number}   [options.minIntervalMs=30000]  floor between automatic
 *                                      revalidations — stops focus-thrash when
 *                                      alt-tabbing rapidly
 * @param {number}   [options.pollMs=0]  0 = no polling
 * @param {boolean}  [options.enabled=true]
 * @param {Function} [options.validate]  data => boolean. A result that fails
 *                                      validation is treated EXACTLY like a
 *                                      failed fetch: on a background
 *                                      revalidation the last good data stays on
 *                                      screen and `error` is set — a malformed
 *                                      HTTP-200 must never overwrite a good
 *                                      board and clear the error.
 */
export function useServerData(fetcher, options = {}) {
  const {
    deps = [],
    revalidateOnFocus = true,
    minIntervalMs = 30_000,
    pollMs = 0,
    enabled = true,
    validate,
  } = options;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [isValidating, setIsValidating] = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  // Refs so the effects below never need these in their dep arrays.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const inFlightRef = useRef(false);   // never run two fetches at once
  const lastAtRef = useRef(0);         // for minIntervalMs
  const mountedRef = useRef(true);
  // Generation counter — bumped every time deps reset. A fetch only APPLIES its
  // result if its generation is still current; the fetcher takes no abort
  // signal, so this is the minimal correct stale-response guard.
  const genRef = useRef(0);
  // A forced call arriving while a fetch is in flight (deps changed, or the
  // user hit refresh) must not be silently dropped — queue it; the settling
  // fetch re-runs it immediately.
  const pendingRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /**
   * @param {object} [opts]
   * @param {boolean} [opts.background] true = revalidation; keep showing the
   *        current data instead of flashing a spinner (stale-while-revalidate).
   * @param {boolean} [opts.force] bypass the minInterval floor (user-initiated).
   */
  const run = useCallback(async (opts = {}) => {
    const { background = false, force = false } = opts;
    if (!enabled) return;
    if (inFlightRef.current) {
      // Collapse concurrent calls — but a FORCED call (deps reset, explicit
      // refresh) carries intent that must survive: queue it so the in-flight
      // fetch re-runs it when it settles. Dropping it was the bug where a deps
      // change mid-flight left the OLD request's result on screen forever.
      if (force) pendingRef.current = opts;
      return;
    }
    if (!force && Date.now() - lastAtRef.current < minIntervalMs) return;

    const gen = genRef.current;
    inFlightRef.current = true;
    background ? setIsValidating(true) : setLoading(true);
    try {
      const result = await fetcherRef.current();
      if (!mountedRef.current) return;                     // unmounted mid-flight
      if (genRef.current !== gen) return;                  // stale generation — deps changed underneath us
      const check = validateRef.current;
      if (typeof check === 'function' && !check(result)) {
        // Malformed HTTP-200. Treat exactly like a failed fetch: never let a
        // background revalidation overwrite good data with garbage.
        const invalid = new Error('Server returned an invalid payload');
        invalid.payload = result;
        setError(invalid);
        if (!background) setData(null);
        return;
      }
      setData(result);
      setError(null);
      setFetchedAt(new Date());
      lastAtRef.current = Date.now();
    } catch (e) {
      if (!mountedRef.current) return;
      if (genRef.current !== gen) return;                  // stale generation — its error is moot too
      // A failed REVALIDATION must not blank out good data on screen. Surface
      // the error, keep rendering what we have.
      setError(e);
      if (!background) setData(null);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
        setIsValidating(false);
        const queued = pendingRef.current;
        pendingRef.current = null;
        if (queued) run(queued);                           // the deferred forced fetch, now current-gen
      } else {
        pendingRef.current = null;
      }
    }
  }, [enabled, minIntervalMs]);

  /** Explicit user action — always fetches, ignores the interval floor. */
  const refresh = useCallback(() => run({ background: true, force: true }), [run]);

  // Initial load (and whenever deps change).
  useEffect(() => {
    if (!enabled) return;
    genRef.current += 1;                // invalidate any in-flight result
    lastAtRef.current = 0;              // deps changed => this is a real load
    run({ background: false, force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  // The reconciler: you looked at the tab again, so re-read the surface.
  useEffect(() => {
    if (!enabled || !revalidateOnFocus) return;
    const onWake = () => {
      if (document.visibilityState === 'visible') run({ background: true });
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [enabled, revalidateOnFocus, run]);

  // Optional polling — off unless a caller genuinely needs live data.
  useEffect(() => {
    if (!enabled || !pollMs) return;
    const id = setInterval(() => run({ background: true }), pollMs);
    return () => clearInterval(id);
  }, [enabled, pollMs, run]);

  return { data, error, loading, isValidating, fetchedAt, refresh };
}

export default useServerData;
